import { createAsyncThunk } from '@reduxjs/toolkit'
import { useGraphCacheStore } from '@tunasong/graph-cache'
import type { StoredObject } from '@tunasong/models'
import type { Audio, Entity, EntityType, Persisted } from '@tunasong/schemas'
import { isAudio } from '@tunasong/schemas'
import invariant from 'tiny-invariant'
import { entitiesApi } from '../../api/entities.js'
import { mediaApi } from '../../api/media.js'
import { storageApi } from '../../api/storage.js'
import type { RootState } from '../../configure-store.js'
import type { TunaThunkDispatch } from '../../hooks/thunk-dispatch.hook.js'
import { createObjectPath } from '../../util.js'
import type { HostKind } from '../config/config-slice.js'
import { notificationsSlice } from '../notifications/notifications-slice.js'

/**
 * `audio/analyze` is a cloud-only route: the local kernel does not mount it. Firing it
 * after a local upload turns a 501 into a "Failed to load data" toast that contradicts a
 * successful upload, so only ever analyze audio when the host is the cloud.
 */
export function shouldAnalyzeAudio(entity: Persisted<Entity>, hostKind: HostKind): entity is Persisted<Audio> {
  return isAudio(entity) && hostKind === 'cloud'
}

const mimeLoader = import('./mime-types.js')

/** The MIME type for a file name, from its extension. `undefined` when the extension is unknown. */
export const lookupMimeType = async (name: string) => (await mimeLoader).lookup(name) || undefined

interface UploadResult {
  filename: string
}

/** Upload the blob to storage. */
export const uploadBlob = createAsyncThunk<
  UploadResult,
  {
    entityId: string
    name: string
    blob: Blob
    defaultMimeType?: string
    metadata?: Record<string, string>
    entityType?: EntityType
  },
  { state: RootState; dispatch: TunaThunkDispatch }
>(
  'storage/uploadBlob',
  async (
    { entityId, name, blob, defaultMimeType = 'audio/wav', metadata, entityType },
    { rejectWithValue, getState, dispatch }
  ) => {
    const userId = getState().user.userId

    invariant(userId, 'User must be logged in to upload a file')

    /** Upload the actual file */
    const mime = await mimeLoader
    const mimeType = (blob.type ? blob.type : mime.lookup(name)) || defaultMimeType
    const data = await blob.arrayBuffer()

    const filename = createObjectPath({ entityId, userId, entityType })
    /**
     * SHA256 signature required
     * @see https://github.com/brix/crypto-js/issues/91
     * @see https://github.com/brix/crypto-js
     * @see https://docs.aws.amazon.com/AmazonCloudFront/latest/DeveloperGuide/private-content-restricting-access-to-s3.html#private-content-origin-access-identity-signature-version-4
     */

    // await API.upload(token, filename, data, contentType)

    /** Get the upload URL */
    const {
      data: signedUrl,
      isError,
      error,
    } = await dispatch(storageApi.endpoints.getUploadUrl.initiate({ filename, contentType: mimeType, metadata }))

    if (isError || !signedUrl) {
      console.log("Couldn't get signed URL", error)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const message = (error as any).data?.message ?? 'Unable to authorize upload of file'
      dispatch(
        notificationsSlice.actions.setAlert({
          severity: 'error',
          title: 'Upload failed',
          message: `Unable to authorize upload of file: ${message ? message : 'Unknown error'}`,
        })
      )
      return rejectWithValue('Unable to authorize upload of file')
    }

    /** Upload the actual file. If the upload fails, the promise should be rejected */
    await dispatch(
      storageApi.endpoints.uploadFile.initiate({ signedUrl: signedUrl.url, data, contentType: mimeType })
    ).unwrap()

    return { filename }
  }
)

export const uploadStoredEntity = createAsyncThunk<
  Persisted<Entity>,
  {
    id: string
    name: string
    type: EntityType
    parentId: string | null
    blob: Blob
    message?: string
    isPrivate?: boolean
    tags?: string[]
  },
  { state: RootState; dispatch: TunaThunkDispatch }
>(
  'storage/uploadStoredEntity',
  async (
    { id, name, parentId, type, blob, message = 'Uploading media...', isPrivate = false, tags },
    { getState, dispatch }
  ) => {
    const userId = getState().user.userId
    if (!userId) {
      throw new Error(`Upload requires an authenticated user`)
    }
    if (!blob.type) {
      throw new Error("Blob must have a valid 'type' property")
    }
    invariant(type, 'Entity type must be provided')

    dispatch(notificationsSlice.actions.setAlert({ severity: 'info', message }))

    /** We create the id client side, because we need to tag the blob with it */
    const metadata = { entityId: id }

    const { filename } = await dispatch(uploadBlob({ entityId: id, name, blob, metadata, entityType: type })).unwrap()

    const storedObject = {
      id,
      name,
      type,
      parentId: parentId ? parentId : undefined,
      tags,
      storage: {
        type: 'S3',
        filename,
      },
    } satisfies StoredObject<Entity>

    const { upsertCache, upsertPlaceholder, retain, release } = useGraphCacheStore.getState()
    /**
     * Update the cache optimistically. A placeholder already cached (the upload manager's, which shows the file
     * from its blob URL) is kept, and any server version replaces this one. GH #2652
     */
    const tempCachedEntity = {
      ...storedObject,
      userId,
      updatedAt: new Date().toISOString(),
      createdAt: new Date().toISOString(),
    }

    upsertPlaceholder(tempCachedEntity)
    // The placeholder is all the UI has of the upload until the create settles, so eviction must keep it. GH #1643
    retain(id)

    let result: Persisted<Entity>
    try {
      // Load the parent
      const parent = parentId
        ? await dispatch(entitiesApi.endpoints.loadEntity.initiate({ id: parentId })).unwrap()
        : null

      const entity = { ...storedObject, parentId: parentId ?? undefined }
      result = await dispatch(entitiesApi.endpoints.createEntity.initiate({ entity, parent, isPrivate })).unwrap()
      // The create response carries `storageUrls`; until it is cached, the file shows only the placeholder. GH #2652
      upsertCache(result)
    } finally {
      release(id)
    }
    /**
     * Now the S3 object is available. The clip is saved whether or not analysis succeeds (it can be
     * retried from the block), so the upload does not wait for it.
     */
    if (shouldAnalyzeAudio(result, getState().config.hostKind)) {
      void dispatch(mediaApi.endpoints.analyzeAudio.initiate(result))
    }
    return result
  }
)
